import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, CheckCircle, TrendingUp, TrendingDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DailyReportCard } from "../../types";

interface ReportCardTeaserProps {
  report: DailyReportCard;
  onOpen: () => void;
}

const GRADE_GRADIENTS: Record<string, string> = {
  A: "from-emerald-400 to-green-500",
  B: "from-blue-400 to-indigo-500",
  C: "from-amber-400 to-yellow-500",
  D: "from-orange-400 to-red-400",
};

const ReportCardTeaser: React.FC<ReportCardTeaserProps> = ({ report, onOpen }) => {
  const { t } = useTranslation();

  return (
    <div className="px-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        onClick={onOpen}
        className="card p-4 cursor-pointer active:scale-[0.98] transition-transform"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                GRADE_GRADIENTS[report.grade] || "from-red-500 to-rose-600"
              } flex items-center justify-center text-xl shadow-card`}
            >
              {report.gradeEmoji}
            </div>
            <div>
              <h3 className="text-sm font-bold text-content-primary">{t("reportCard.title")}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-lg font-black text-content-primary">{report.grade}</span>
                <span className="text-xs text-content-tertiary">
                  {report.calorieScore.consumed}/{report.calorieScore.goal} kcal
                </span>
                {report.calorieScore.status === "on_target" ? (
                  <CheckCircle size={14} className="text-success" />
                ) : report.calorieScore.status === "over" ? (
                  <TrendingUp size={14} className="text-danger" />
                ) : (
                  <TrendingDown size={14} className="text-warning" />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!report.isPremium && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md gradient-warm text-white">
                PRO
              </span>
            )}
            <ChevronRight size={18} className="text-content-tertiary" />
          </div>
        </div>

        {report.isPremium && report.highlights.length > 0 && (
          <p className="text-xs text-content-tertiary mt-2 truncate">✨ {report.highlights[0]}</p>
        )}

        {!report.isPremium && (
          <p className="text-[11px] text-warning mt-2 font-medium">{t("reportCard.tapForMore")}</p>
        )}
      </motion.div>
    </div>
  );
};

export default ReportCardTeaser;
